import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
    Alert,
    Button,
    Calendar,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Dropdown,
    Empty,
    Modal,
    Row,
    Segmented,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Typography,
    message,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ApartmentOutlined,
    CalendarOutlined,
    DeleteOutlined,
    DownOutlined,
    DownloadOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    GlobalOutlined,
    HistoryOutlined,
    MoreOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    RightOutlined,
    RobotOutlined,
    ScanOutlined,
    StopOutlined,
    UpOutlined,
} from '@ant-design/icons';
import AreaMap from '../components/Map/AreaMap';
import MissionEditorView from '../components/Operations/MissionEditorView';
import OperationTimeline from '../components/Operations/OperationTimeline';
import OperationBuilder from '../components/Operations/OperationBuilder';
import DeviceFleetPanel from '../components/Operations/DeviceFleetPanel';
import DeviceCommandLog from '../components/Operations/DeviceCommandLog';
import { useAuth } from '../context/AuthContext';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import {
    formatStatusLabel,
    statusColorMap,
    OPERATION_ENVIRONMENT_OPTIONS,
    type AreaItem,
    type DeviceItem,
    type MapPath,
    type MissionCommandItem,
    type MissionEditorMode,
    type MissionItem,
    type MissionPointItem,
    type MissionSavePayload,
    type OperationEnvironment,
} from '../components/Operations/missionModels';
import type { OperationItem } from '../components/Operations/operationModels';
import { operationStatusColor } from '../components/Operations/operationModels';
import { buildAreaPathMap } from '../components/Operations/missionAreaUtils';
import { getMissionPointMarkerLabel } from '../components/Operations/missionPointLabels';
import {
    clearMissionPatternHandoff,
    readMissionPatternHandoff,
    type MissionPatternHandoff,
} from '../components/Operations/missionPatternHandoff';
import { getHeartbeatAwareDeviceStatus } from '../components/Operations/deviceStatusSnapshot';
import '../style/Operations.css';

const { Text, Title } = Typography;
type MissionArchiveFilter = 'active' | 'archived';

const OPERATIONS_TAB_ORDER = ['missions', 'operations', 'fleet', 'commands'];
const MISSION_STATUS_ROW_CLASS: Record<string, string> = {
    draft: 'operations-mission-row-draft',
    scheduled: 'operations-mission-row-scheduled',
    running: 'operations-mission-row-running',
    completed: 'operations-mission-row-completed',
    cancelled: 'operations-mission-row-cancelled',
    archived: 'operations-mission-row-archived',
};

const getMissionRowClassName = (mission: MissionItem, selectedMissionId: number | null): string => (
    [
        'operations-mission-status-row',
        'operations-clickable-row',
        MISSION_STATUS_ROW_CLASS[String(mission.status || '').toLowerCase()] || 'operations-mission-row-unknown',
        mission.id === selectedMissionId ? 'operations-selected-row' : '',
    ].filter(Boolean).join(' ')
);

const formatValidationErrors = (value: unknown): string | null => {
    if (!Array.isArray(value) || value.length === 0) {
        return null;
    }

    const messages = value
        .flatMap((item) => {
            if (!item || typeof item !== 'object') {
                return [];
            }

            const candidate = item as {
                constraints?: Record<string, string> | null;
                children?: unknown[];
            };
            const constraintMessages = candidate.constraints
                ? Object.values(candidate.constraints)
                : [];
            const childMessages = formatValidationErrors(candidate.children);

            return [
                ...constraintMessages,
                ...(childMessages ? [childMessages] : []),
            ];
        })
        .map((message) => String(message).trim())
        .filter(Boolean);

    return messages.length > 0 ? messages.join(' ') : null;
};

const getErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        if (typeof responseData === 'string' && responseData.trim()) {
            const trimmed = responseData.trim();
            if (/^<!doctype html/i.test(trimmed) || /<html[\s>]/i.test(trimmed)) {
                return fallback;
            }
            return trimmed;
        }

        const validationMessage = formatValidationErrors(responseData);
        if (validationMessage) {
            return validationMessage;
        }

        return (
            (responseData as any)?.message ||
            (responseData as any)?.error ||
            error.message ||
            fallback
        );
    }
    if (error instanceof Error) {
        return error.message;
    }
    return fallback;
};

const isCancelledRequest = (error: unknown) => (
    axios.isCancel(error) || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')
);

const logRequestError = (label: string, error: unknown) => {
    if (!axios.isAxiosError(error)) {
        console.error(label, error);
        return;
    }

    const method = error.config?.method?.toUpperCase() || 'GET';
    const url = error.config?.url || 'unknown endpoint';
    const status = error.response?.status ? `HTTP ${error.response.status}` : 'no response';
    console.error(`${label}: ${method} ${url} (${status})`, error.response?.data ?? error.message);
};

const getCollectionLoadError = (error: unknown, collectionName: string) => {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
        return `${collectionName} could not be loaded because the API endpoint returned 404 (not found). This is a service error, not an empty list.`;
    }

    return getErrorMessage(error, `${collectionName} could not be loaded. Please try again.`);
};

const parseApiList = <T,>(payload: any): T[] => {
    if (Array.isArray(payload?.data)) {
        return payload.data as T[];
    }
    if (Array.isArray(payload)) {
        return payload as T[];
    }
    return [];
};

const formatTimestamp = (value?: string | null) => {
    if (!value) {
        return <Text type="secondary">Not set</Text>;
    }

    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return <Text type="secondary">Unknown</Text>;
    }

    return <Text title={parsed.toISOString()}>{parsed.format('DD MMM YYYY HH:mm')}</Text>;
};

const parsePointGeometry = (geom: any): { lat: number; lng: number } | null => {
    if (!geom?.coordinates || !Array.isArray(geom.coordinates) || geom.coordinates.length < 2) {
        return null;
    }

    const lng = Number(geom.coordinates[0]);
    const lat = Number(geom.coordinates[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    return { lat, lng };
};

const Operations: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [missions, setMissions] = React.useState<MissionItem[]>([]);
    const [areas, setAreas] = React.useState<AreaItem[]>([]);
    const [devices, setDevices] = React.useState<DeviceItem[]>([]);
    const [selectedMissionId, setSelectedMissionId] = React.useState<number | null>(null);
    const [selectedMission, setSelectedMission] = React.useState<MissionItem | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [referencesLoading, setReferencesLoading] = React.useState(false);
    const [referencesError, setReferencesError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [editorMode, setEditorMode] = React.useState<MissionEditorMode | null>(null);
    const [editorMission, setEditorMission] = React.useState<MissionItem | null>(null);
    const [editorLoading, setEditorLoading] = React.useState(false);
    const [initialEnvironment, setInitialEnvironment] = React.useState<OperationEnvironment | undefined>(undefined);
    const [resumeAreaId, setResumeAreaId] = React.useState<number | null>(null);
    const [missionPatternHandoff, setMissionPatternHandoff] = React.useState<{
        draftKey: string;
        value: MissionPatternHandoff;
    } | null>(null);
    const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);
    // Indoor anchor marker state (slam_scan missions only)
    const [markerStatus, setMarkerStatus] = React.useState<string | null>(null);
    const [markerLoading, setMarkerLoading] = React.useState(false);

    // ── Orchestration state ──────────────────────────────────────────────────
    const [activeTab, setActiveTab] = React.useState<string>('missions');
    const [operations, setOperations] = React.useState<OperationItem[]>([]);
    const [operationsLoading, setOperationsLoading] = React.useState(false);
    const [operationsError, setOperationsError] = React.useState<string | null>(null);
    const [missionsError, setMissionsError] = React.useState<string | null>(null);
    const [operationBuilderOpen, setOperationBuilderOpen] = React.useState(false);
    const [operationSaving, setOperationSaving] = React.useState(false);
    const [expandedOperationId, setExpandedOperationId] = React.useState<number | null>(null);
    const [selectedCalendarDate, setSelectedCalendarDate] = React.useState<Dayjs>(() => dayjs());
    const [missionArchiveFilter, setMissionArchiveFilter] = React.useState<MissionArchiveFilter>('active');
    const [scheduleMission, setScheduleMission] = React.useState<MissionItem | null>(null);
    const [scheduleAt, setScheduleAt] = React.useState<Dayjs | null>(null);
    const [scheduleSaving, setScheduleSaving] = React.useState(false);

    const currentCompanyId = React.useMemo(() => (
        currentUser?.currentCompanyId ||
        currentUser?.companyId ||
        currentUser?.companyMemberships?.[0]?.company?.id ||
        null
    ), [currentUser]);

    const resumeMissionDraftKey = searchParams.get('resumeMissionDraft');

    React.useEffect(() => {
        if (!resumeMissionDraftKey || !currentCompanyId || missionPatternHandoff?.draftKey === resumeMissionDraftKey) {
            return;
        }

        const handoff = readMissionPatternHandoff(resumeMissionDraftKey);
        if (!handoff) {
            message.error('The temporary mission draft could not be restored. Please start the mission again.');
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('resumeMissionDraft');
            setSearchParams(nextParams, { replace: true });
            return;
        }

        if (handoff.companyId && handoff.companyId !== currentCompanyId) {
            clearMissionPatternHandoff(resumeMissionDraftKey);
            message.error('The mission draft belongs to a different company and was not restored.');
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('resumeMissionDraft');
            setSearchParams(nextParams, { replace: true });
            return;
        }

        const environment = handoff.draft.formValues.operationEnvironment as OperationEnvironment | undefined;
        setMissionPatternHandoff({ draftKey: resumeMissionDraftKey, value: handoff });
        setEditorMission(null);
        setEditorLoading(false);
        setInitialEnvironment(environment);
        setEditorMode('create');
    }, [currentCompanyId, missionPatternHandoff?.draftKey, resumeMissionDraftKey, searchParams, setSearchParams]);

    const handleMissionPatternResumeConsumed = React.useCallback(() => {
        if (!missionPatternHandoff) return;
        clearMissionPatternHandoff(missionPatternHandoff.draftKey);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('resumeMissionDraft');
        setSearchParams(nextParams, { replace: true });
    }, [missionPatternHandoff, searchParams, setSearchParams]);

    const loadReferences = React.useCallback(async (signal?: AbortSignal) => {
        if (!currentCompanyId) {
            setAreas([]);
            setDevices([]);
            setReferencesError(null);
            return;
        }

        setReferencesLoading(true);
        const [areasResult, devicesResult] = await Promise.allSettled([
            axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, signal }),
            axios.get(ENDPOINTS.GET_DEVICES, {
                withCredentials: true,
                signal,
                params: {
                    assignment_status: 'mine',
                    company_id: currentCompanyId,
                },
            }),
        ]);

        if (signal?.aborted) {
            return;
        }

        const errors: string[] = [];
        if (areasResult.status === 'fulfilled') {
            setAreas(parseApiList<AreaItem>(areasResult.value.data));
        } else if (!isCancelledRequest(areasResult.reason)) {
            setAreas([]);
            logRequestError('Failed to load operation areas', areasResult.reason);
            errors.push(getCollectionLoadError(areasResult.reason, 'Areas'));
        }

        if (devicesResult.status === 'fulfilled') {
            setDevices(parseApiList<DeviceItem>(devicesResult.value.data));
        } else if (!isCancelledRequest(devicesResult.reason)) {
            setDevices([]);
            logRequestError('Failed to load operation devices', devicesResult.reason);
            errors.push(getCollectionLoadError(devicesResult.reason, 'Devices'));
        }

        setReferencesError(errors.length > 0 ? errors.join(' ') : null);
        setReferencesLoading(false);
    }, [currentCompanyId]);

    const loadMissions = React.useCallback(async (signal?: AbortSignal) => {
        if (!currentCompanyId) {
            setMissions([]);
            setMissionsError(null);
            return;
        }

        setLoading(true);
        setMissionsError(null);
        try {
            const params: Record<string, any> = { limit: 100 };
            if (missionArchiveFilter === 'archived') {
                params.status = 'archived';
            }
            const response = await axios.get(ENDPOINTS.MISSIONS, {
                withCredentials: true,
                signal,
                params,
            });
            const nextMissions = parseApiList<MissionItem>(response.data);
            setMissions(nextMissions);
            setSelectedMissionId((current) => {
                if (current && nextMissions.some((mission) => mission.id === current)) {
                    return current;
                }
                return nextMissions[0]?.id ?? null;
            });
        } catch (error) {
            if (isCancelledRequest(error)) {
                return;
            }
            setMissions([]);
            setSelectedMissionId(null);
            setSelectedMission(null);
            setMissionsError(getCollectionLoadError(error, 'Missions'));
            logRequestError('Failed to load missions', error);
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [currentCompanyId, missionArchiveFilter]);

    const loadMissionDetail = React.useCallback(async (missionId: number) => {
        setDetailLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.MISSION_DETAIL(missionId), {
                withCredentials: true,
            });
            setSelectedMission(response.data?.data || null);
        } catch (error) {
            console.error('Failed to load mission detail', error);
            message.error(getErrorMessage(error, 'Failed to load mission detail'));
            setSelectedMission(null);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const resumeMissionIdParam = searchParams.get('resumeMissionId');
    const createdAreaIdParam = searchParams.get('createdAreaId');

    React.useEffect(() => {
        if (!resumeMissionIdParam || !currentCompanyId) return;

        const missionId = Number(resumeMissionIdParam);
        const areaId = Number(createdAreaIdParam);
        if (!Number.isInteger(missionId) || missionId <= 0) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('resumeMissionId');
            nextParams.delete('createdAreaId');
            setSearchParams(nextParams, { replace: true });
            return;
        }

        let cancelled = false;
        setEditorMode('edit');
        setEditorLoading(true);
        setInitialEnvironment(undefined);
        setMissionPatternHandoff(null);
        setResumeAreaId(Number.isInteger(areaId) && areaId > 0 ? areaId : null);

        axios.get(ENDPOINTS.MISSION_DETAIL(missionId), { withCredentials: true })
            .then((response) => {
                if (cancelled) return;
                setSelectedMissionId(missionId);
                setEditorMission(response.data?.data || null);
            })
            .catch((error) => {
                if (cancelled) return;
                setEditorMode(null);
                setEditorMission(null);
                setResumeAreaId(null);
                message.error(getErrorMessage(error, 'Failed to resume mission editing'));
            })
            .finally(() => {
                if (cancelled) return;
                setEditorLoading(false);
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('resumeMissionId');
                nextParams.delete('createdAreaId');
                setSearchParams(nextParams, { replace: true });
            });

        return () => {
            cancelled = true;
        };
    }, [createdAreaIdParam, currentCompanyId, resumeMissionIdParam, searchParams, setSearchParams]);

    // ── Operations CRUD ──────────────────────────────────────────────────────
    const loadOperations = React.useCallback(async (signal?: AbortSignal) => {
        if (!currentCompanyId) {
            setOperations([]);
            setOperationsError(null);
            return;
        }

        setOperationsLoading(true);
        setOperationsError(null);
        try {
            const res = await axios.get(ENDPOINTS.OPERATIONS, { withCredentials: true, signal });
            const list = parseApiList<OperationItem>(res.data);
            setOperations(list);
        } catch (err) {
            if (isCancelledRequest(err)) {
                return;
            }
            setOperations([]);
            setOperationsError(getCollectionLoadError(err, 'Operations'));
            logRequestError('Failed to load operations', err);
        } finally {
            if (!signal?.aborted) {
                setOperationsLoading(false);
            }
        }
    }, [currentCompanyId]);

    const handleCreateOperation = React.useCallback(async (payload: {
        name: string;
        description?: string;
        operationType?: string;
        config?: Record<string, any> | null;
        scheduledStartAt?: string | null;
        missions: Array<{
            missionId: number;
            deviceId?: number;
            executionOrder: number;
            triggerType?: string;
            triggerCondition?: string;
            dependsOnOperationMissionIds?: number[];
        }>;
    }) => {
        setOperationSaving(true);
        try {
            await axios.post(ENDPOINTS.OPERATIONS, payload, { withCredentials: true });

            message.success('Operation created');
            setOperationBuilderOpen(false);
            void loadOperations();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to create operation');
        } finally {
            setOperationSaving(false);
        }
    }, [loadOperations]);

    const handleStartOperation = React.useCallback(async (opId: number) => {
        try {
            await axios.post(ENDPOINTS.OPERATION_START(opId), {}, { withCredentials: true });
            message.success('Operation sent to robot');
            void loadOperations();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to send operation');
        }
    }, [loadOperations]);

    const handleCancelOperation = React.useCallback(async (opId: number) => {
        try {
            await axios.post(ENDPOINTS.OPERATION_CANCEL(opId), {}, { withCredentials: true });
            message.success('Operation cancelled');
            void loadOperations();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to cancel operation');
        }
    }, [loadOperations]);

    const buildSingleMissionOperationPayload = React.useCallback((
        mission: MissionItem,
        scheduledStartAt: string,
        mode: 'start_now' | 'scheduled'
    ) => ({
        name: `${mission.name} - ${mode === 'start_now' ? 'Send' : 'Scheduled'} ${dayjs(scheduledStartAt).format('DD MMM YYYY HH:mm')}`,
        description: `Single mission operation for ${mission.name}`,
        operationType: 'single_mission',
        scheduledStartAt,
        config: {
            source: mode === 'start_now' ? 'mission_start_button' : 'mission_schedule_button',
            missionTemplateId: mission.id,
        },
        missions: [{
            missionId: mission.id,
            executionOrder: 1,
            triggerType: 'auto',
            triggerCondition: 'auto',
            dependsOnOperationMissionIds: [],
        }],
    }), []);

    const handleStartMissionAsOperation = React.useCallback(async (mission: MissionItem) => {
        try {
            setSaving(true);
            const scheduledStartAt = new Date().toISOString();
            const createResponse = await axios.post(
                ENDPOINTS.OPERATIONS,
                buildSingleMissionOperationPayload(mission, scheduledStartAt, 'start_now'),
                { withCredentials: true }
            );
            const operation = createResponse.data?.data || createResponse.data;
            if (!operation?.id) {
                throw new Error('Operation was created without an id');
            }

            await axios.post(ENDPOINTS.OPERATION_START(operation.id), {}, { withCredentials: true });
            message.success('Operation created and sent to robot');
            setActiveTab('operations');
            setExpandedOperationId(operation.id);
            await loadOperations();
            await loadMissions();
            if (selectedMissionId) {
                await loadMissionDetail(selectedMissionId);
            }
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to send mission operation'));
        } finally {
            setSaving(false);
        }
    }, [buildSingleMissionOperationPayload, loadMissionDetail, loadMissions, loadOperations, selectedMissionId]);

    const handleScheduleMissionAsOperation = React.useCallback(async () => {
        if (!scheduleMission || !scheduleAt) {
            message.warning('Select a schedule date and time');
            return;
        }

        try {
            setScheduleSaving(true);
            const createResponse = await axios.post(
                ENDPOINTS.OPERATIONS,
                buildSingleMissionOperationPayload(scheduleMission, scheduleAt.toISOString(), 'scheduled'),
                { withCredentials: true }
            );
            const operation = createResponse.data?.data || createResponse.data;
            message.success('Operation scheduled');
            setScheduleMission(null);
            setScheduleAt(null);
            setActiveTab('operations');
            setExpandedOperationId(operation?.id ?? null);
            await loadOperations();
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to schedule mission operation'));
        } finally {
            setScheduleSaving(false);
        }
    }, [buildSingleMissionOperationPayload, loadOperations, scheduleAt, scheduleMission]);

    const areaPathMap = React.useMemo(() => buildAreaPathMap(areas), [areas]);

    const getAreaLabel = React.useCallback((areaId?: number | null, fallbackName?: string | null) => {
        if (areaId && areaPathMap.has(areaId)) {
            return areaPathMap.get(areaId) as string;
        }
        return fallbackName || 'Unknown area';
    }, [areaPathMap]);

    const operationColumns = React.useMemo<ColumnsType<OperationItem>>(() => [
        {
            title: 'Operation',
            key: 'name',
            render: (_, op) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{op.name}</Text>
                    {op.description && <Text type="secondary" style={{ fontSize: 11 }}>{op.description}</Text>}
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (val: string) => <Tag color={operationStatusColor(val)}>{val}</Tag>,
        },
        {
            title: 'Missions',
            key: 'missionCount',
            width: 90,
            render: (_, op) => <Tag>{op.operationMissions?.length ?? 0} steps</Tag>,
        },
        {
            title: 'Area',
            key: 'area',
            width: 140,
            render: (_, op) => op.area?.name || (op.areaId ? getAreaLabel(op.areaId) : '—'),
        },
        {
            title: 'Measurements',
            key: 'measurements',
            width: 150,
            render: (_, op) => (
                <Space direction="vertical" size={2}>
                    <Text>{op.measurementCount ?? 0}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {op.lastMeasurementAt ? dayjs(op.lastMeasurementAt).format('DD MMM YYYY HH:mm') : 'No readings'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Scheduled',
            dataIndex: 'scheduledStartAt',
            width: 150,
            render: (val: string | null) => val ? dayjs(val).format('DD MMM YYYY HH:mm') : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 220,
            render: (_, op) => (
                <Space size={4}>
                    <Button size="small" icon={<EyeOutlined />}
                        onClick={(e) => { e.stopPropagation(); navigate(`/operations/${op.id}`); }}>
                        View
                    </Button>
                    {(op.status === 'draft' || op.status === 'planned') && (
                        <Button size="small" type="primary" icon={<PlayCircleOutlined />}
                            onClick={(e) => { e.stopPropagation(); void handleStartOperation(op.id); }}>
                            Send
                        </Button>
                    )}
                    {(op.status === 'planned' || op.status === 'dispatching' || op.status === 'running') && (
                        <Button size="small" danger icon={<StopOutlined />}
                            onClick={(e) => { e.stopPropagation(); void handleCancelOperation(op.id); }}>
                            Cancel
                        </Button>
                    )}
                </Space>
            ),
        },
    ], [getAreaLabel, handleStartOperation, handleCancelOperation, navigate]);

    const openOperationForDate = React.useCallback((value: Dayjs) => {
        const now = dayjs();
        const scheduledStart = value.isSame(now, 'day')
            ? now.add(1, 'hour').second(0).millisecond(0)
            : value.hour(9).minute(0).second(0).millisecond(0);

        navigate(`/operations/new?scheduledStartAt=${encodeURIComponent(scheduledStart.toISOString())}`);
    }, [navigate]);

    const renderScheduledOperationsForDate = React.useCallback((value: Dayjs) => {
        const dayOperations = operations.filter((operation) => (
            operation.scheduledStartAt && dayjs(operation.scheduledStartAt).isSame(value, 'day')
        ));
        const isSelectedDay = selectedCalendarDate.isSame(value, 'day');

        return (
            <div className="operations-calendar-events">
                {dayOperations.slice(0, 4).map((operation) => (
                    <button
                        key={operation.id}
                        type="button"
                        className={`operations-calendar-entry operations-calendar-entry-${operation.status}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/operations/${operation.id}`);
                        }}
                    >
                        <span className="operations-calendar-dot" />
                        <span className="operations-calendar-time">
                            {dayjs(operation.scheduledStartAt).format('HH:mm')}
                        </span>
                        <span className="operations-calendar-name">
                            {operation.name}
                        </span>
                    </button>
                ))}
                {dayOperations.length > 4 ? (
                    <button
                        type="button"
                        className="operations-calendar-more"
                        onClick={(event) => event.stopPropagation()}
                    >
                        +{dayOperations.length - 4} more
                    </button>
                ) : null}
                {isSelectedDay ? (
                    <button
                        type="button"
                        className="operations-calendar-add"
                        aria-label={`Create operation on ${value.format('D MMMM YYYY')}`}
                        title="Create operation on this day"
                        onClick={(event) => {
                            event.stopPropagation();
                            openOperationForDate(value);
                        }}
                    >
                        <PlusOutlined />
                        <span>New operation</span>
                    </button>
                ) : null}
            </div>
        );
    }, [navigate, openOperationForDate, operations, selectedCalendarDate]);

    React.useEffect(() => {
        const controller = new AbortController();
        void loadReferences(controller.signal);
        return () => controller.abort();
    }, [loadReferences]);

    React.useEffect(() => {
        const controller = new AbortController();
        void loadMissions(controller.signal);
        return () => controller.abort();
    }, [loadMissions]);

    React.useEffect(() => {
        const controller = new AbortController();
        void loadOperations(controller.signal);
        return () => controller.abort();
    }, [loadOperations]);

    React.useEffect(() => {
        if (!selectedMissionId) {
            setSelectedMission(null);
            return;
        }

        void loadMissionDetail(selectedMissionId);
    }, [loadMissionDetail, selectedMissionId]);

    const selectedMissionMapPoints = React.useMemo(() => (
        (selectedMission?.missionPoints || [])
            .map((point, index) => ({
                id: point.id,
                lat: point.resolvedLocation?.lat ?? NaN,
                lng: point.resolvedLocation?.lng ?? NaN,
                label: getMissionPointMarkerLabel(selectedMission?.missionPoints || [], point, index),
            }))
            .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    ), [selectedMission]);

    const selectedMissionMapPaths = React.useMemo<MapPath[]>(() => (
        (selectedMission?.missionLegs || [])
            .map((leg) => ({
                id: leg.id,
                selected: leg.status === 'generated' || leg.status === 'radius_fallback',
                warning: leg.status === 'radius_fallback',
                coordinates: Array.isArray(leg.pathGeom?.coordinates)
                    ? leg.pathGeom.coordinates
                        .map((coordinate: any) => ({
                            lng: Number(coordinate?.[0]),
                            lat: Number(coordinate?.[1]),
                        }))
                        .filter((coordinate: any) => Number.isFinite(coordinate.lng) && Number.isFinite(coordinate.lat))
                    : [],
            }))
            .filter((path) => path.coordinates.length >= 2)
    ), [selectedMission]);

    const selectedMissionDeviceMarker = React.useMemo(() => {
        const location = parsePointGeometry(selectedMission?.device?.lastLocation);
        if (!location) {
            return [];
        }

        return [{
            id: selectedMission?.device?.id || 'device',
            lat: location.lat,
            lng: location.lng,
            label: selectedMission?.device?.label || 'Robot',
            selected: true,
        }];
    }, [selectedMission]);

    const closeEditorView = React.useCallback(() => {
        setEditorMode(null);
        setEditorMission(null);
        setEditorLoading(false);
        setInitialEnvironment(undefined);
        setResumeAreaId(null);
        setMissionPatternHandoff(null);
    }, []);

    const openCreateEditor = React.useCallback(() => {
        setEditorMission(null);
        setEditorLoading(false);
        setInitialEnvironment(undefined);
        setResumeAreaId(null);
        setEditorMode('create');
    }, []);

    const openCreateEditorWithEnvironment = React.useCallback((env: OperationEnvironment) => {
        setEditorMission(null);
        setEditorLoading(false);
        setInitialEnvironment(env);
        setResumeAreaId(null);
        setEditorMode('create');
    }, []);

    const openEditEditor = React.useCallback(async () => {
        if (!selectedMissionId) {
            return;
        }

        setEditorMode('edit');
        setResumeAreaId(null);

        if (selectedMission?.id === selectedMissionId && Array.isArray(selectedMission.missionPoints)) {
            setEditorMission(selectedMission);
            setEditorLoading(false);
            return;
        }

        setEditorLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.MISSION_DETAIL(selectedMissionId), {
                withCredentials: true,
            });
            setEditorMission(response.data?.data || null);
        } catch (error) {
            setEditorMode(null);
            message.error(getErrorMessage(error, 'Failed to load mission for editing'));
        } finally {
            setEditorLoading(false);
        }
    }, [selectedMission, selectedMissionId]);

    const handleSaveMission = React.useCallback(async (payload: MissionSavePayload, missionId?: number) => {
        setSaving(true);
        try {
            // Strip slamMapIds from the REST payload before sending (they are handled separately)
            const { slamMapIds, ...missionPayload } = payload;

            const response = missionId
                ? await axios.put(ENDPOINTS.MISSION_DETAIL(missionId), missionPayload, { withCredentials: true })
                : await axios.post(ENDPOINTS.MISSIONS, missionPayload, { withCredentials: true });

            const savedMission = response.data?.data as MissionItem;
            const savedMissionId = savedMission?.id ?? missionId;

            // Attach SLAM maps after save (for indoor/mixed missions)
            if (savedMissionId && slamMapIds && slamMapIds.length > 0) {
                await Promise.allSettled(
                    slamMapIds.map((slamMapId) =>
                        axios.post(
                            ENDPOINTS.MISSION_SLAM_MAPS(savedMissionId),
                            { slamMapId },
                            { withCredentials: true }
                        ).catch((e) => {
                            console.warn(`Failed to attach SLAM map ${slamMapId} to mission ${savedMissionId}`, e);
                        })
                    )
                );
            }

            message.success(missionId ? 'Mission updated' : 'Mission created');
            closeEditorView();
            await loadMissions();
            if (savedMissionId) {
                setSelectedMissionId(savedMissionId);
                await loadMissionDetail(savedMissionId);
            }
        } catch (error) {
            console.error('Failed to save mission', {
                error,
                payload,
                responseData: axios.isAxiosError(error) ? error.response?.data : undefined,
            });
            throw error;
        } finally {
            setSaving(false);
        }
    }, [closeEditorView, loadMissionDetail, loadMissions]);

    const handleArchiveMission = React.useCallback(async (mission: MissionItem) => {
        setSaving(true);
        try {
            await axios.post(ENDPOINTS.MISSION_ARCHIVE(mission.id), {}, { withCredentials: true });
            message.success('Mission archived');
            setDetailsModalOpen(false);
            if (selectedMissionId === mission.id) {
                setSelectedMission(null);
            }
            await loadMissions();
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to archive mission'));
        } finally {
            setSaving(false);
        }
    }, [loadMissions, selectedMissionId]);

    const handleRestoreMission = React.useCallback(async (mission: MissionItem) => {
        setSaving(true);
        try {
            await axios.post(ENDPOINTS.MISSION_RESTORE(mission.id), {}, { withCredentials: true });
            message.success('Mission restored');
            setDetailsModalOpen(false);
            if (selectedMissionId === mission.id) {
                setSelectedMission(null);
            }
            await loadMissions();
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to restore mission'));
        } finally {
            setSaving(false);
        }
    }, [loadMissions, selectedMissionId]);

    const showArchiveInsteadConfirm = React.useCallback((mission: MissionItem) => {
        Modal.confirm({
            title: 'Archive mission instead?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <div style={{ marginBottom: 8 }}>
                        <strong>{mission.name}</strong> is used by an operation, so it cannot be permanently deleted.
                    </div>
                    <Alert
                        type="info"
                        showIcon
                        message="Archive hides it without breaking history"
                        description="The mission will disappear from active missions and remain available from the archived filter."
                    />
                </div>
            ),
            okText: 'Archive',
            onOk: () => handleArchiveMission(mission),
        });
    }, [handleArchiveMission]);

    const handleDeleteMission = React.useCallback((mission: MissionItem) => {
        Modal.confirm({
            title: 'Delete mission',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <div style={{ marginBottom: 8 }}>
                        Delete <strong>{mission.name}</strong>? This removes the mission template and its route setup.
                    </div>
                    <Alert
                        type="warning"
                        showIcon
                        message="Operation history is kept"
                        description="Missions already used by operations cannot be deleted until the related operations are deleted."
                    />
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                let offeredArchive = false;
                setSaving(true);
                try {
                    await axios.delete(ENDPOINTS.MISSION_DETAIL(mission.id), { withCredentials: true });
                    message.success('Mission deleted');
                    setDetailsModalOpen(false);
                    if (selectedMissionId === mission.id) {
                        setSelectedMission(null);
                    }
                    await loadMissions();
                } catch (error) {
                    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                    const errorMessage = getErrorMessage(error, 'Failed to delete mission');
                    if (status === 409 && /used by operations/i.test(errorMessage)) {
                        offeredArchive = true;
                        setSaving(false);
                        showArchiveInsteadConfirm(mission);
                        return;
                    }
                    message.error(getErrorMessage(error, 'Failed to delete mission'));
                } finally {
                    if (!offeredArchive) {
                        setSaving(false);
                    }
                }
            },
        });
    }, [loadMissions, selectedMissionId, showArchiveInsteadConfirm]);

    // Load existing marker status when a slam_scan mission is selected
    React.useEffect(() => {
        if (!selectedMission || selectedMission.missionType !== 'slam_scan' || !selectedMission.areaId) {
            setMarkerStatus(null);
            return;
        }
        let cancelled = false;
        setMarkerLoading(true);
        axios.get(ENDPOINTS.AREA_INDOOR_MARKERS(selectedMission.areaId), { withCredentials: true })
            .then((res) => {
                if (cancelled) return;
                const markers = Array.isArray(res.data?.data) ? res.data.data : [];
                if (markers.length > 0) {
                    setMarkerStatus(markers[0].status as string);
                } else {
                    setMarkerStatus(null);
                }
            })
            .catch(() => { if (!cancelled) setMarkerStatus(null); })
            .finally(() => { if (!cancelled) setMarkerLoading(false); });
        return () => { cancelled = true; };
    }, [selectedMission]);

    const downloadAnchorMarker = React.useCallback(async () => {
        if (!selectedMission) return;
        setMarkerLoading(true);
        try {
            // Generate marker if not yet existing (idempotent)
            await axios.post(
                ENDPOINTS.MISSION_INDOOR_MARKERS_GENERATE(selectedMission.id),
                {},
                { withCredentials: true }
            );
            setMarkerStatus('printed');

            // Download the PDF sheet
            const pdfRes = await axios.get(
                ENDPOINTS.MISSION_INDOOR_MARKERS_SHEET(selectedMission.id),
                { withCredentials: true, responseType: 'blob' }
            );
            const disposition = pdfRes.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const fileName = match?.[1] || `oscar-anchor-marker-mission${selectedMission.id}.pdf`;
            const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Anchor marker PDF downloaded');
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to download anchor marker'));
        } finally {
            setMarkerLoading(false);
        }
    }, [selectedMission]);

    const exportArduPilot = React.useCallback(async (format: 'qgc_plan' | 'qgc_wpl') => {
        if (!selectedMissionId) return;
        try {
            const response = await axios.get(
                ENDPOINTS.MISSION_EXPORT_ARDUPILOT(selectedMissionId, format),
                { withCredentials: true, responseType: 'blob' }
            );
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const fileName = match?.[1] || (format === 'qgc_wpl' ? 'mission.waypoints' : 'mission.plan');
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            message.success(`Exported as ${fileName}`);
        } catch (error) {
            message.error(getErrorMessage(error, 'Export failed'));
        }
    }, [selectedMissionId]);

    const missionColumns = React.useMemo<ColumnsType<MissionItem>>(() => [
        {
            title: 'Mission',
            key: 'mission',
            width: 220,
            render: (_, mission) => (
                <div className="operations-cell-stack">
                    <Text strong className="operations-cell-primary" title={mission.name}>{mission.name}</Text>
                    <Text type="secondary" className="operations-cell-secondary" title={mission.description || mission.missionType || 'Robot mission'}>
                        {mission.description || mission.missionType || 'Robot mission'}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (value: string, mission) => (
                <Space direction="vertical" size={2}>
                    <Tag color={value === 'draft' ? 'gold' : statusColorMap[value] || 'default'}>
                        {formatStatusLabel(value)}
                    </Tag>
                    {mission.externalSource === 'external_api' && !mission.areaId ? (
                        <Tag color="orange">Needs setup</Tag>
                    ) : null}
                </Space>
            ),
        },
        {
            title: 'Environment',
            key: 'operationEnvironment',
            width: 125,
            render: (_: any, record: any) => {
                const env = record.operationEnvironment || 'outdoor';
                const color = env === 'outdoor' ? 'green' : env === 'indoor' ? 'orange' : 'blue';
                const label = OPERATION_ENVIRONMENT_OPTIONS.find((o) => o.value === env)?.label || 'Outdoor (GPS)';
                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: 'Robot',
            key: 'robot',
            width: 150,
            render: (_, mission) => (
                <Text className="operations-cell-primary" title={mission.device?.label || 'Unknown device'}>
                    {mission.device?.label || 'Unknown device'}
                </Text>
            ),
        },
        {
            title: 'Field',
            key: 'field',
            width: 230,
            render: (_, mission) => (
                <div className="operations-cell-stack">
                    <Text className="operations-cell-primary">
                        {mission.areaId
                            ? getAreaLabel(mission.areaId, mission.area?.name)
                            : mission.externalSource === 'external_api'
                                ? 'Area required'
                                : 'Unassigned scan'}
                    </Text>
                    {mission.pattern?.name ? (
                        <Text type="secondary" className="operations-cell-secondary">{mission.pattern.name}</Text>
                    ) : (
                        <Text type="secondary" className="operations-cell-secondary">No pattern</Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Route',
            key: 'route',
            width: 145,
            render: (_, mission) => (
                <div className="operations-cell-stack">
                    <Text className="operations-cell-primary">{mission.routeSummary?.totalDistanceKm ?? 0} km</Text>
                    <Text type="secondary" className="operations-cell-secondary">
                        {mission.routeSummary?.pointCount ?? 0} points / {mission.routeSummary?.legCount ?? 0} legs
                    </Text>
                    <Text type="secondary" className="operations-cell-secondary">
                        Radius {mission.routeConfig?.turnRadiusMeters ?? 3} m
                    </Text>
                </div>
            ),
        },
    ], [getAreaLabel]);

    const detailPointColumns = React.useMemo<ColumnsType<MissionPointItem>>(() => [
        {
            title: '#',
            dataIndex: 'pointIndex',
            key: 'pointIndex',
            width: 72,
        },
        {
            title: 'Label',
            dataIndex: 'label',
            key: 'label',
            render: (value: string | null | undefined, point) => value || `Step ${point.pointIndex}`,
        },
        {
            title: 'Action',
            dataIndex: 'actionType',
            key: 'actionType',
            render: (value: string) => <Tag>{value}</Tag>,
        },
        {
            title: 'Source',
            key: 'source',
            render: (_, point) => point.patternPointId ? (
                <Tag color="blue">
                    Pattern #{point.patternPoint?.pointIndex ?? point.patternPointId}
                </Tag>
            ) : (
                <Tag>Custom</Tag>
            ),
        },
        {
            title: 'Coordinates',
            key: 'coordinates',
            render: (_, point) => point.resolvedLocation ? (
                <Text>{point.resolvedLocation.lat.toFixed(6)}, {point.resolvedLocation.lng.toFixed(6)}</Text>
            ) : (
                <Text type="secondary">No GPS</Text>
            ),
        },
    ], []);

    const detailCommandColumns = React.useMemo<ColumnsType<MissionCommandItem>>(() => [
        {
            title: '#',
            dataIndex: 'seq',
            key: 'seq',
            width: 72,
        },
        {
            title: 'Point',
            key: 'missionPointIndex',
            width: 92,
            render: (_, command) => command.missionPointIndex ? (
                <Tag color="blue">P{command.missionPointIndex}</Tag>
            ) : (
                <Text type="secondary">Global</Text>
            ),
        },
        {
            title: 'Kind',
            dataIndex: 'kind',
            key: 'kind',
            width: 110,
            render: (value: string) => <Tag>{value}</Tag>,
        },
        {
            title: 'Command',
            key: 'commandName',
            render: (_, command) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{command.commandName}</Text>
                    {command.label ? <Text type="secondary">{command.label}</Text> : null}
                </Space>
            ),
        },
        {
            title: 'Code / Frame',
            key: 'metadata',
            width: 160,
            render: (_, command) => (
                <Space direction="vertical" size={2}>
                    <Text>{command.commandCode ?? '-'}</Text>
                    <Text type="secondary">Frame {command.frame ?? '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Source',
            key: 'source',
            width: 120,
            render: (_, command) => <Tag>{command.source || 'manual'}</Tag>,
        },
    ], []);

    const renderMissionExpandedDetail = React.useCallback((missionRow: MissionItem) => {
        const detail = selectedMission?.id === missionRow.id ? selectedMission : null;
        const isArchived = detail?.status === 'archived';
        const missionPoints = detail?.missionPoints || [];
        const setupRequirements = detail?.externalSource === 'external_api'
            ? [
                !detail.areaId ? 'Select an area' : null,
                !missionPoints.some((point) => point.actionType === 'start') ? 'Add a START point' : null,
                !missionPoints.some((point) => point.actionType === 'end') ? 'Add an END point' : null,
            ].filter((item): item is string => Boolean(item))
            : [];
        const missionNeedsSetup = setupRequirements.length > 0;
        const visiblePreviewPoints = missionPoints.length <= 7
            ? missionPoints
            : [...missionPoints.slice(0, 5), missionPoints[missionPoints.length - 1]];
        const hiddenPreviewPointCount = Math.max(0, missionPoints.length - visiblePreviewPoints.length);
        const moreActionItems: MenuProps['items'] = [];
        if (!isArchived && !missionNeedsSetup) {
            moreActionItems.push({ key: 'edit', icon: <EditOutlined />, label: 'Edit mission' });
        }
        moreActionItems.push({ key: 'details', icon: <EyeOutlined />, label: 'Full mission details' });
        moreActionItems.push({
            key: 'export',
            icon: <DownloadOutlined />,
            label: detail?.missionType === 'slam_scan' ? 'Download marker' : 'Export route',
        });
        if (!isArchived) {
            moreActionItems.push({ type: 'divider' });
            moreActionItems.push({ key: 'delete', icon: <DeleteOutlined />, label: 'Delete mission', danger: true });
        }

        const handleMoreAction: MenuProps['onClick'] = ({ key }) => {
            if (!detail) return;
            if (key === 'edit') {
                void openEditEditor();
            } else if (key === 'details') {
                setDetailsModalOpen(true);
            } else if (key === 'export') {
                if (detail.missionType === 'slam_scan') {
                    void downloadAnchorMarker();
                } else {
                    void exportArduPilot('qgc_plan');
                }
            } else if (key === 'delete') {
                handleDeleteMission(detail);
            }
        };

        return (
            <div className="operations-expanded-detail">
                <Spin spinning={detailLoading || saving}>
                    {!detail ? (
                        <Empty description="Loading mission detail" />
                    ) : (
                        <div className="operations-expanded-compact">
                            <div className="operations-expanded-preview-grid">
                                <section className="operations-expanded-preview-panel operations-expanded-map-panel">
                                    <div className="operations-expanded-preview-heading">
                                        <div>
                                            <Text strong>Route map</Text>
                                            <Text type="secondary">
                                                {detail.areaId
                                                    ? getAreaLabel(detail.areaId, detail.area?.name)
                                                    : 'Autonomous scan area'}
                                            </Text>
                                        </div>
                                        <Tag color="blue">
                                            {detail.routeSummary?.totalDistanceKm ?? 0} km
                                        </Tag>
                                    </div>
                                    <div className="operations-expanded-map">
                                        <AreaMap
                                            areaGeometry={detail.area?.geom}
                                            areaName={detail.areaId
                                                ? getAreaLabel(detail.areaId, detail.area?.name)
                                                : 'Autonomous scan area'}
                                            points={selectedMissionMapPoints}
                                            paths={selectedMissionMapPaths}
                                            measurements={selectedMissionDeviceMarker}
                                            mapKey={`mission-row-preview-${detail.id}`}
                                            alwaysShowMap
                                            emptyOverlayMessage="No fixed route is required for this mission."
                                        />
                                    </div>
                                </section>

                                <section className="operations-expanded-preview-panel operations-expanded-plan-panel">
                                    <div className="operations-expanded-preview-heading">
                                        <div>
                                            <Text strong>Mission preview</Text>
                                            <Text type="secondary">
                                                {detail.description || 'Route and actions the robot will execute'}
                                            </Text>
                                        </div>
                                    </div>

                                    <div className="operations-expanded-preview-stats">
                                        <div>
                                            <Text type="secondary">Points</Text>
                                            <Text strong>{detail.routeSummary?.pointCount ?? missionPoints.length}</Text>
                                        </div>
                                        <div>
                                            <Text type="secondary">Legs</Text>
                                            <Text strong>{detail.routeSummary?.legCount ?? detail.missionLegs?.length ?? 0}</Text>
                                        </div>
                                        <div>
                                            <Text type="secondary">Commands</Text>
                                            <Text strong>{detail.missionCommands?.length ?? 0}</Text>
                                        </div>
                                    </div>

                                    {visiblePreviewPoints.length > 0 ? (
                                        <div className="operations-expanded-point-list">
                                            {visiblePreviewPoints.map((point, index) => {
                                                const pointCommands = (detail.missionCommands || []).filter((command) => (
                                                    command.missionPointId === point.id
                                                    || command.missionPointIndex === point.pointIndex
                                                ));
                                                const missionPointIndex = missionPoints.findIndex((candidate) => candidate.id === point.id);
                                                const isLastPreviewPoint = index === visiblePreviewPoints.length - 1;

                                                return (
                                                    <React.Fragment key={point.id}>
                                                        {hiddenPreviewPointCount > 0 && isLastPreviewPoint ? (
                                                            <div className="operations-expanded-point-gap">
                                                                {hiddenPreviewPointCount} intermediate points
                                                            </div>
                                                        ) : null}
                                                        <div className="operations-expanded-point">
                                                            <span className="operations-expanded-point-marker">
                                                                {getMissionPointMarkerLabel(
                                                                    missionPoints,
                                                                    point,
                                                                    missionPointIndex >= 0 ? missionPointIndex : index
                                                                )}
                                                            </span>
                                                            <div className="operations-expanded-point-copy">
                                                                <Text strong>{point.label || `Point ${point.pointIndex}`}</Text>
                                                                <Text type="secondary">
                                                                    {formatStatusLabel(point.actionType)}
                                                                    {pointCommands.length > 0
                                                                        ? ` · ${pointCommands.length} command${pointCommands.length === 1 ? '' : 's'}`
                                                                        : ''}
                                                                </Text>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={detail.missionType === 'slam_scan'
                                                ? 'The robot will explore and build the route autonomously.'
                                                : 'No route points have been added yet.'}
                                        />
                                    )}
                                </section>

                                <section className="operations-expanded-preview-panel operations-expanded-action-panel">
                                    <div className="operations-expanded-preview-heading">
                                        <div>
                                            <Text strong>Mission actions</Text>
                                            <Text type="secondary">Next step for this mission</Text>
                                        </div>
                                        <Tag color={missionNeedsSetup ? 'orange' : isArchived ? 'default' : 'green'}>
                                            {missionNeedsSetup ? 'Setup required' : isArchived ? 'Archived' : 'Ready'}
                                        </Tag>
                                    </div>
                                    <div className="operations-expanded-action-body">
                                        {missionNeedsSetup ? (
                                            <div className="operations-expanded-action-state operations-expanded-action-state-warning">
                                                <ExclamationCircleOutlined />
                                                <div>
                                                    <Text strong>Finish this API draft</Text>
                                                    <Text type="secondary">
                                                        {detail.externalReference
                                                            ? `Imported as ${detail.externalReference}. Complete the missing setup before it can run.`
                                                            : 'Complete the missing setup before this imported mission can run.'}
                                                    </Text>
                                                    <ul>
                                                        {setupRequirements.map((requirement) => (
                                                            <li key={requirement}>{requirement}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        ) : isArchived ? (
                                            <div className="operations-expanded-action-state">
                                                <HistoryOutlined />
                                                <div>
                                                    <Text strong>Mission archived</Text>
                                                    <Text type="secondary">Restore it before editing or running it again.</Text>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="operations-expanded-action-state operations-expanded-action-state-ready">
                                                <PlayCircleOutlined />
                                                <div>
                                                    <Text strong>Ready for execution</Text>
                                                    <Text type="secondary">Send it now or choose a scheduled start.</Text>
                                                </div>
                                            </div>
                                        )}

                                        <div className="operations-expanded-action-buttons">
                                            {isArchived ? (
                                                <Button
                                                    size="middle"
                                                    type="primary"
                                                    block
                                                    icon={<ReloadOutlined />}
                                                    onClick={() => void handleRestoreMission(detail)}
                                                    loading={saving}
                                                >
                                                    Restore mission
                                                </Button>
                                            ) : missionNeedsSetup ? (
                                                <Button
                                                    size="middle"
                                                    type="primary"
                                                    block
                                                    icon={<EditOutlined />}
                                                    onClick={() => void openEditEditor()}
                                                    loading={saving}
                                                >
                                                    Complete setup
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="middle"
                                                        type="primary"
                                                        block
                                                        icon={<PlayCircleOutlined />}
                                                        onClick={() => void handleStartMissionAsOperation(detail)}
                                                        loading={saving}
                                                    >
                                                        Send mission
                                                    </Button>
                                                    <Button
                                                        size="middle"
                                                        block
                                                        icon={<CalendarOutlined />}
                                                        onClick={() => {
                                                            setScheduleMission(detail);
                                                            setScheduleAt(dayjs().add(1, 'hour'));
                                                        }}
                                                        loading={saving}
                                                    >
                                                        Schedule
                                                    </Button>
                                                </>
                                            )}
                                            <Dropdown
                                                menu={{ items: moreActionItems, onClick: handleMoreAction }}
                                                trigger={['click']}
                                                placement="bottomRight"
                                            >
                                                <Button size="middle" block icon={<MoreOutlined />} loading={markerLoading || saving}>
                                                    More actions
                                                </Button>
                                            </Dropdown>
                                            <Button
                                                size="middle"
                                                type="text"
                                                block
                                                icon={<UpOutlined />}
                                                className="operations-expanded-collapse"
                                                onClick={() => setSelectedMissionId(null)}
                                            >
                                                Collapse preview
                                            </Button>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}
                </Spin>
            </div>
        );
    }, [
        detailLoading,
        downloadAnchorMarker,
        exportArduPilot,
        getAreaLabel,
        handleDeleteMission,
        handleRestoreMission,
        handleStartMissionAsOperation,
        markerLoading,
        markerStatus,
        openEditEditor,
        saving,
        selectedMission,
        selectedMissionDeviceMarker,
        selectedMissionMapPaths,
        selectedMissionMapPoints,
    ]);

    const renderReadOnlyMissionDetails = React.useCallback(() => {
        if (!selectedMission) {
            return <Empty description="Select a mission to inspect its details" />;
        }

        const deviceStatus = selectedMission.device
            ? getHeartbeatAwareDeviceStatus(selectedMission.device).displayStatus
            : null;
        const missionNeedsSetup = selectedMission.externalSource === 'external_api' && (
            !selectedMission.areaId
            || !(selectedMission.missionPoints || []).some((point) => point.actionType === 'start')
            || !(selectedMission.missionPoints || []).some((point) => point.actionType === 'end')
        );

        return (
            <Spin spinning={detailLoading || saving}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {missionNeedsSetup ? (
                        <Alert
                            type="warning"
                            showIcon
                            message="Imported mission needs setup"
                            description="Open the Mission Editor, select an area, and add START and END points before sending or scheduling this mission."
                        />
                    ) : null}
                    <Space wrap>
                        <Tag color={statusColorMap[selectedMission.status] || 'default'}>
                            {selectedMission.status}
                        </Tag>
                        {deviceStatus ? (
                            <Tag color={statusColorMap[deviceStatus] || 'default'}>
                                Robot {formatStatusLabel(deviceStatus)}
                            </Tag>
                        ) : null}
                        {selectedMission.pattern?.name ? (
                            <Tag>{selectedMission.pattern.name}</Tag>
                        ) : (
                            <Tag>No pattern</Tag>
                        )}
                        {selectedMission.missionType === 'slam_scan' ? (
                            markerLoading ? (
                                <Tag>Marker checking...</Tag>
                            ) : markerStatus ? (
                                <Tag color="orange">Marker {markerStatus}</Tag>
                            ) : (
                                <Tag>Marker not generated</Tag>
                            )
                        ) : null}
                    </Space>

                    <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
                        <Descriptions.Item label="Mission">{selectedMission.name}</Descriptions.Item>
                        <Descriptions.Item label="Robot">
                            {selectedMission.device?.label || 'Unknown'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Field">
                            {selectedMission.areaId
                                ? getAreaLabel(selectedMission.areaId, selectedMission.area?.name)
                                : selectedMission.externalSource === 'external_api'
                                    ? 'Area required'
                                    : 'Unassigned scan'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Scheduled start">
                            {formatTimestamp(selectedMission.scheduledStartAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Turn radius">
                            {selectedMission.routeConfig?.turnRadiusMeters ?? 3} m
                        </Descriptions.Item>
                        <Descriptions.Item label="Last heartbeat">
                            {formatTimestamp(selectedMission.device?.lastHeartbeat)}
                        </Descriptions.Item>
                    </Descriptions>

                    {selectedMission.description ? (
                        <Text type="secondary">{selectedMission.description}</Text>
                    ) : null}

                    <Row gutter={[12, 12]}>
                        <Col xs={12} md={8}>
                            <Card size="small">
                                <Statistic title="Points" value={selectedMission.routeSummary?.pointCount ?? 0} />
                            </Card>
                        </Col>
                        <Col xs={12} md={8}>
                            <Card size="small">
                                <Statistic title="Commands" value={selectedMission.missionCommands?.length ?? 0} />
                            </Card>
                        </Col>
                        <Col xs={12} md={8}>
                            <Card size="small">
                                <Statistic
                                    title="Distance (km)"
                                    value={selectedMission.routeSummary?.totalDistanceKm ?? 0}
                                    precision={2}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Card className="operations-map-card" size="small" styles={{ body: { padding: 12 } }}>
                        <div className="operations-map-wrapper operations-detail-modal-map">
                            <AreaMap
                                areaGeometry={selectedMission.area?.geom}
                                areaName={selectedMission.areaId
                                    ? getAreaLabel(selectedMission.areaId, selectedMission.area?.name)
                                    : selectedMission.externalSource === 'external_api'
                                        ? 'Area required'
                                        : 'Unassigned scan'}
                                points={selectedMissionMapPoints}
                                paths={selectedMissionMapPaths}
                                measurements={selectedMissionDeviceMarker}
                            />
                        </div>
                    </Card>

                    {selectedMission.missionType === 'slam_scan' ? (
                        <Card size="small" title="Indoor anchor marker (AprilTag)">
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                <Space wrap align="center">
                                    <Text type="secondary">Marker status:</Text>
                                    {markerLoading ? (
                                        <Tag>Checking...</Tag>
                                    ) : markerStatus === 'confirmed' ? (
                                        <Tag color="green">Confirmed</Tag>
                                    ) : markerStatus === 'detected' ? (
                                        <Tag color="blue">Detected by robot</Tag>
                                    ) : markerStatus === 'printed' ? (
                                        <Tag color="orange">Printed - awaiting scan</Tag>
                                    ) : (
                                        <Tag>Not yet generated</Tag>
                                    )}
                                </Space>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    This marker is used as a fixed reference point during the SLAM scan.
                                </Text>
                            </Space>
                        </Card>
                    ) : null}

                    <Table<MissionPointItem>
                        rowKey="id"
                        className="operations-table"
                        columns={detailPointColumns}
                        dataSource={selectedMission.missionPoints || []}
                        pagination={false}
                        size="small"
                        scroll={{ x: 720 }}
                    />

                    <Table<MissionCommandItem>
                        rowKey="id"
                        className="operations-table"
                        columns={detailCommandColumns}
                        dataSource={selectedMission.missionCommands || []}
                        pagination={false}
                        size="small"
                        scroll={{ x: 760 }}
                    />
                </Space>
            </Spin>
        );
    }, [
        detailCommandColumns,
        detailLoading,
        detailPointColumns,
        getAreaLabel,
        markerLoading,
        markerStatus,
        saving,
        selectedMission,
        selectedMissionDeviceMarker,
        selectedMissionMapPaths,
        selectedMissionMapPoints,
    ]);

    if (!currentCompanyId) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Select a company before opening operations."
            />
        );
    }

    // Helper: get access token for child components
    const accessToken = (currentUser as any)?.accessToken || '';

    return (
        <div className="operations-page">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {editorMode ? (
                    <MissionEditorView
                        mode={editorMode}
                        mission={editorMission}
                        loading={editorLoading}
                        saving={saving}
                        areas={areas}
                        devices={devices}
                        referencesLoading={referencesLoading}
                        resumeAreaId={resumeAreaId}
                        onCancel={closeEditorView}
                        onSave={handleSaveMission}
                        initialEnvironment={initialEnvironment}
                        missionPatternHandoff={missionPatternHandoff}
                        onMissionPatternResumeConsumed={handleMissionPatternResumeConsumed}
                    />
                ) : (
                    <>
                        <Row justify="space-between" align="middle" className="operations-header">
                            <Col>
                                <Space direction="vertical" size={0}>
                                    <Title level={2} style={{ margin: 0 }}>Operations</Title>
                                    <Text type="secondary" className="operations-subtitle">
                                        Monitor deployed robots, inspect missions, and manage field execution
                                    </Text>
                                </Space>
                            </Col>
                        </Row>

                        <Tabs
                            activeKey={activeTab}
                            onChange={(nextTab) => {
                                setActiveTab(nextTab);
                                if (nextTab !== 'missions' && missionArchiveFilter !== 'active') {
                                    setMissionArchiveFilter('active');
                                }
                            }}
                            type="card"
                            className="operations-tabs"
                            items={[
                                {
                                    key: 'operations',
                                    label: <span><ApartmentOutlined /> Operations</span>,
                                    children: (
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <Card size="small" className="operations-description-card">
                                                <Space direction="vertical" size={4}>
                                                    <Text strong>Operations are execution runs.</Text>
                                                    <Text type="secondary">
                                                        Use an operation to schedule, start, and track one or more reusable missions. Operations keep the run history, dispatch sequence, timing, status, and per-mission execution results.
                                                    </Text>
                                                </Space>
                                            </Card>
                                            {operationsError ? (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message="Operations are unavailable"
                                                    description={operationsError}
                                                    action={(
                                                        <Button size="small" onClick={() => void loadOperations()}>
                                                            Retry
                                                        </Button>
                                                    )}
                                                />
                                            ) : null}
                                            <Card
                                                size="small"
                                                title="Scheduled operations calendar"
                                                className="operations-calendar-card"
                                            >
                                                <Calendar
                                                    className="operations-schedule-calendar"
                                                    value={selectedCalendarDate}
                                                    onChange={setSelectedCalendarDate}
                                                    cellRender={(current, info) => (
                                                        info.type === 'date'
                                                            ? renderScheduledOperationsForDate(current)
                                                            : info.originNode
                                                    )}
                                                />
                                            </Card>
                                            <Card
                                                size="small"
                                                title="Multi-device operations"
                                                extra={
                                                    <Space>
                                                        <Button icon={<ReloadOutlined />} size="small"
                                                            onClick={() => void loadOperations()} loading={operationsLoading}>
                                                            Refresh
                                                        </Button>
                                                         <Button type="primary" icon={<PlusOutlined />} size="small"
                                                            onClick={() => navigate('/operations/new')}>
                                                            New operation
                                                        </Button>
                                                    </Space>
                                                }
                                            >
                                                <Table<OperationItem>
                                                    rowKey="id"
                                                    columns={operationColumns}
                                                    dataSource={operations}
                                                    loading={operationsLoading}
                                                    size="small"
                                                    pagination={{ pageSize: 10, size: 'small' }}
                                                    locale={{
                                                        emptyText: (
                                                            <Empty
                                                                description={
                                                                    operationsError
                                                                        ? 'Operations could not be loaded'
                                                                        : 'No operations yet — create one to orchestrate multiple missions'
                                                                }
                                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            />
                                                        ),
                                                    }}
                                                    expandable={{
                                                        expandedRowKeys: expandedOperationId ? [expandedOperationId] : [],
                                                        onExpand: (expanded, record) => setExpandedOperationId(expanded ? record.id : null),
                                                        expandedRowRender: (op) => (
                                                            <div style={{ padding: '8px 0' }}>
                                                                <OperationTimeline operationMissions={op.operationMissions || []} />
                                                            </div>
                                                        ),
                                                    }}
                                                    scroll={{ x: 950 }}
                                                />
                                            </Card>
                                            <OperationBuilder
                                                open={operationBuilderOpen}
                                                onClose={() => setOperationBuilderOpen(false)}
                                                onSave={handleCreateOperation}
                                                missions={missions}
                                                devices={devices}
                                                saving={operationSaving}
                                            />
                                        </Space>
                                    ),
                                },
                                {
                                    key: 'missions',
                                    label: <span><ScanOutlined /> Missions</span>,
                                    children: (
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <Card size="small" className="operations-description-card">
                                                <Space direction="vertical" size={4}>
                                                    <Text strong>Missions are reusable task templates.</Text>
                                                    <Text type="secondary">
                                                        A mission defines the route, commands, device assignment, and module configuration. Sending or scheduling a mission creates a new operation run, so the same mission can be executed repeatedly without losing history.
                                                    </Text>
                                                </Space>
                                            </Card>
                                            {missionsError || referencesError ? (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message="Mission data is unavailable"
                                                    description={[missionsError, referencesError].filter(Boolean).join(' ')}
                                                    action={(
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                void loadReferences();
                                                                void loadMissions();
                                                            }}
                                                        >
                                                            Retry
                                                        </Button>
                                                    )}
                                                />
                                            ) : null}

                        {/* Quick-create mission shortcuts */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Card
                                    hoverable
                                    className="operations-env-card"
                                    onClick={openCreateEditor}
                                >
                                    <Space direction="vertical" align="center" style={{ width: '100%', textAlign: 'center' }}>
                                        <GlobalOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                                        <Text strong>Define mission</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Select an area and configure route, commands, and device assignment
                                        </Text>
                                        <Tag color="green" icon={<PlusOutlined />}>New mission</Tag>
                                    </Space>
                                </Card>
                            </Col>
                            <Col xs={24} md={12}>
                                <Card
                                    hoverable
                                    className="operations-env-card"
                                    onClick={() => openCreateEditorWithEnvironment('scan')}
                                >
                                    <Space direction="vertical" align="center" style={{ width: '100%', textAlign: 'center' }}>
                                        <ScanOutlined style={{ fontSize: 32, color: '#722ed1' }} />
                                        <Text strong>Scan mission</Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            No area needed — autonomous exploration
                                        </Text>
                                        <Tag color="purple" icon={<PlusOutlined />}>Create</Tag>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Card
                                    className="operations-table-card"
                                    title="Robot missions"
                                    extra={(
                                        <Space wrap>
                                            <Segmented
                                                size="small"
                                                value={missionArchiveFilter}
                                                onChange={(value) => {
                                                    setSelectedMissionId(null);
                                                    setSelectedMission(null);
                                                    setMissionArchiveFilter(value as MissionArchiveFilter);
                                                }}
                                                options={[
                                                    { label: 'Active', value: 'active' },
                                                    { label: 'Archived', value: 'archived' },
                                                ]}
                                            />
                                            <Button
                                                icon={<ReloadOutlined />}
                                                onClick={() => {
                                                    void loadReferences();
                                                    void loadMissions();
                                                }}
                                                loading={loading || referencesLoading}
                                            >
                                                Refresh
                                            </Button>
                                            {missionArchiveFilter === 'active' ? (
                                                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEditor}>
                                                    New mission
                                                </Button>
                                            ) : null}
                                        </Space>
                                    )}
                                >
                                    <Spin spinning={loading}>
                                        <Table<MissionItem>
                                            rowKey="id"
                                            className="operations-table"
                                            columns={missionColumns}
                                            dataSource={missions}
                                            pagination={false}
                                            size="middle"
                                            tableLayout="fixed"
                                            locale={{
                                                emptyText: (
                                                    <Empty
                                                        description={
                                                            missionsError
                                                                ? 'Missions could not be loaded'
                                                                : missionArchiveFilter === 'archived'
                                                                ? 'No archived missions'
                                                                : 'No missions created yet'
                                                        }
                                                    />
                                                ),
                                            }}
                                            rowClassName={(mission) => getMissionRowClassName(mission, selectedMissionId)}
                                            expandable={{
                                                expandedRowKeys: selectedMissionId ? [selectedMissionId] : [],
                                                expandRowByClick: true,
                                                columnWidth: 44,
                                                onExpand: (expanded, mission) => {
                                                    setSelectedMissionId(expanded ? mission.id : null);
                                                },
                                                expandIcon: ({ expanded, onExpand, record }) => (
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        className="operations-mission-expand-button"
                                                        icon={expanded ? <DownOutlined /> : <RightOutlined />}
                                                        title={expanded ? 'Collapse mission preview' : 'Expand mission preview'}
                                                        aria-label={expanded ? 'Collapse mission preview' : 'Expand mission preview'}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onExpand(record, event);
                                                        }}
                                                    />
                                                ),
                                                expandedRowRender: renderMissionExpandedDetail,
                                                rowExpandable: () => true,
                                            }}
                                            scroll={{ x: 998 }}
                                        />
                                    </Spin>
                                </Card>
                            </Col>
                        </Row>

                        <Modal
                            open={detailsModalOpen}
                            onCancel={() => setDetailsModalOpen(false)}
                            footer={null}
                            width="min(1120px, 96vw)"
                            title={selectedMission?.name || 'Mission details'}
                            destroyOnClose
                        >
                            {renderReadOnlyMissionDetails()}
                        </Modal>
                                        </Space>
                                    ),
                                },
                                {
                                    key: 'fleet',
                                    label: <span><RobotOutlined /> Devices</span>,
                                    children: (
                                        <DeviceFleetPanel accessToken={accessToken} />
                                    ),
                                },
                                {
                                    key: 'commands',
                                    label: <span><HistoryOutlined /> Command log</span>,
                                    children: (
                                        <DeviceCommandLog accessToken={accessToken} />
                                    ),
                                },
                            ].sort((left, right) => (
                                OPERATIONS_TAB_ORDER.indexOf(left.key) - OPERATIONS_TAB_ORDER.indexOf(right.key)
                            ))}
                        />
                        <Modal
                            open={Boolean(scheduleMission)}
                            onCancel={() => {
                                setScheduleMission(null);
                                setScheduleAt(null);
                            }}
                            onOk={() => void handleScheduleMissionAsOperation()}
                            confirmLoading={scheduleSaving}
                            title={scheduleMission ? `Schedule ${scheduleMission.name}` : 'Schedule mission'}
                            okText="Schedule operation"
                            destroyOnClose
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Text type="secondary">
                                    This creates a reusable operation run for the selected mission. The mission template remains available for future runs.
                                </Text>
                                <DatePicker
                                    showTime
                                    value={scheduleAt}
                                    onChange={setScheduleAt}
                                    style={{ width: '100%' }}
                                    disabledDate={(current) => Boolean(current && current.endOf('day').isBefore(dayjs()))}
                                />
                            </Space>
                        </Modal>
                    </>
                )}
            </Space>
        </div>
    );
};

export default Operations;
